import React, { useEffect, useId, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { AppButton } from '../button/app-button';
import { StatusLabel } from '../../motion';
import { VisuallyHidden } from '../../style-utils/text';
import { layout, palette, radius, spacing, typography } from '../../style-utils/theme';
import { touch } from '../../style-utils/media-queries';

/**
 * COPY ONE VALUE — a door code, a Wi-Fi password, a guide PIN (plan S7.7; the
 * shared primitive the guest guide and the host's stay card both use).
 *
 *   tap → navigator.clipboard.writeText(value)
 *     success → the visible label reads "Copied" for 1800 ms (StatusLabel, so
 *               the button never changes width) and a visually hidden
 *               role="status" says `announce` ("Building code copied")
 *     failure → no clipboard, a refused permission, an insecure origin: a
 *               readonly field holding the value is revealed and selected,
 *               with "Press and hold to copy" — the phone's own copy menu
 *
 * THE VALUE IS A SECRET. It is never logged and never written into an
 * aria-label, a title or a data-* attribute: the only places it exists in the
 * DOM are the page's own text (the caller's) and, after a failure, the
 * readonly field the guest asked for.
 *
 * Double-tap safe: a second tap while a write is in flight is ignored, and a
 * tap during "Copied" restarts the 1800 ms instead of stacking timers.
 */

export const COPIED_MS = 1800;
export const COPIED_LABEL = 'Copied';
export const PRESS_AND_HOLD = 'Press and hold to copy';

const Wrap = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.xs};
  min-width: 0;
  max-width: 100%;
`;

const Fallback = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  width: 100%;
  min-width: 0;
`;

const ValueField = styled.input`
  width: 100%;
  min-width: 0;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surfaceMuted};
  color: ${palette.ink};
  font-family: ${typography.mono};
  font-size: ${typography.size.base}px;
  -webkit-user-select: text;
  user-select: text;

  &:focus {
    outline: none;
    border-color: ${palette.iris};
  }

  ${touch} {
    font-size: ${typography.size.base}px;
  }
`;

const Hint = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export interface CopyValueButtonProps {
  /** What is copied. Never rendered into an attribute. */
  value: string;
  /** The button's words: "Copy building code". */
  label: string;
  /** What the status region says after a copy: "Building code copied". */
  announce: string;
}

const canWrite = (): boolean =>
  typeof navigator !== 'undefined' && !!navigator.clipboard && typeof navigator.clipboard.writeText === 'function';

export const CopyValueButton: React.FC<CopyValueButtonProps> = ({ value, label, announce }) => {
  const [copied, setCopied] = useState(false);
  const [said, setSaid] = useState('');
  const [fallback, setFallback] = useState(false);
  const busy = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const field = useRef<HTMLInputElement | null>(null);
  const hintId = useId();

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  // A new value (the host changed the code, the page refetched) is a new
  // copy: drop "Copied" and any field still holding the old one.
  useEffect(() => {
    setCopied(false);
    setSaid('');
    setFallback(false);
  }, [value]);

  useEffect(() => {
    if (!fallback || !field.current) return;
    field.current.focus();
    field.current.select();
  }, [fallback]);

  const copy = async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      if (!canWrite()) throw new Error('no clipboard');
      await navigator.clipboard.writeText(value);
      setFallback(false);
      setCopied(true);
      setSaid(announce);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        setCopied(false);
        setSaid('');
      }, COPIED_MS);
    } catch {
      setCopied(false);
      setSaid('');
      setFallback(true);
    } finally {
      busy.current = false;
    }
  };

  return (
    <Wrap>
      <AppButton variant="secondary" size="compact" onClick={copy}>
        <StatusLabel state={copied ? 'copied' : 'idle'} labels={{ idle: label, copied: COPIED_LABEL }} />
      </AppButton>
      <VisuallyHidden role="status">{said}</VisuallyHidden>
      {fallback ? (
        <Fallback>
          <ValueField
            ref={field}
            readOnly
            value={value}
            aria-label={label}
            aria-describedby={hintId}
            autoComplete="off"
            spellCheck={false}
            onFocus={e => e.currentTarget.select()}
          />
          <Hint id={hintId}>{PRESS_AND_HOLD}</Hint>
        </Fallback>
      ) : null}
    </Wrap>
  );
};
